import React, { useState } from 'react';
import { Select, Typography, Tooltip } from 'antd';
import { TrendingUp, TrendingDown, MessageCircle, Mail, CheckCircle, XCircle, Reply, Send, Eye, MousePointer } from 'lucide-react';
import './MetricsDisplay.css';

const { Title, Text } = Typography;
const { Option } = Select;

interface WhatsAppStats {
  delivered?: number;
  replied: number;
  sent?: number;
  failed?: number;
  failedPercentage: number;
  completedPercentage: number;
}

interface EmailStats {
  delivered?: number;
  failed?: number;
  sent?: number;
  opened?: number;
  clicked?: number;
  read?: number;
  completed?: number;
  failedPercentage: number;
  completedPercentage: number;
  openRate?: number;
  clickRate?: number;
}

interface StatsData {
  whatsapp?: WhatsAppStats;
  gmail?: EmailStats;
  brevo?: EmailStats;
  zeptomail?: EmailStats;
}

interface MetricsDisplayProps {
  sevenDayOrgStats: StatsData;
  thirtyDayOrgStats: StatsData;
}

interface MetricCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  tooltip?: string;
  percentage?: number;
  isPercentage?: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  icon,
  color,
  tooltip,
  percentage,
  isPercentage = false
}) => {
  const displayValue = isPercentage ? `${value}%` : value;

  // Create lighter color for gradient
  const lightColor = color === '#25D366' ? '#34E77A' :
                    color === '#128C7E' ? '#16A085' :
                    color === '#075E54' ? '#0D7968' :
                    color === '#ff4d4f' ? '#ff7875' :
                    `${color}80`;

  const cardStyle = {
    '--card-color': color,
    '--card-color-light': lightColor
  } as React.CSSProperties;

  return (
    <Tooltip title={tooltip} placement="top">
      <div className="metric-card-compact" style={cardStyle}>
        <div className="metric-icon-compact" style={{ color }}>
          {icon}
        </div>
        <div className="metric-content-compact">
          <div className="metric-value-compact" style={{ color }}>
            {displayValue}
          </div>
          <div className="metric-title-compact">{title}</div>
        </div>
      </div>
    </Tooltip>
  );
};

const MetricsDisplay: React.FC<MetricsDisplayProps> = ({
  sevenDayOrgStats,
  thirtyDayOrgStats
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState<'7' | '30'>('7');

  const currentStats = selectedPeriod === '7' ? sevenDayOrgStats : thirtyDayOrgStats;

  const getWhatsAppMetrics = () => {
    const whatsapp = currentStats.whatsapp;
    if (!whatsapp) return [];

    const metrics = [];

    // Always show WhatsApp messages sent (even if 0)
    metrics.push({
      title: 'WhatsApp Sent',
      value: whatsapp.sent || 0,
      icon: <Send size={20} />,
      color: '#25D366',
      tooltip: 'Total WhatsApp messages sent'
    });

    // Always show delivered (even if 0)
    metrics.push({
      title: 'WhatsApp Delivered',
      value: whatsapp.delivered || 0,
      icon: <CheckCircle size={20} />,
      color: '#128C7E',
      tooltip: 'Total WhatsApp messages delivered'
    });

    // Always show replies
    metrics.push({
      title: 'WhatsApp Replies',
      value: whatsapp.replied || 0,
      icon: <Reply size={20} />,
      color: '#075E54',
      tooltip: 'Total replies received from WhatsApp'
    });

    // Always show failed (even if 0)
    metrics.push({
      title: 'WhatsApp Failed',
      value: whatsapp.failed || 0,
      icon: <XCircle size={20} />,
      color: '#ff4d4f',
      tooltip: 'Total WhatsApp messages that failed to send'
    });

    // Always show success rate
    metrics.push({
      title: 'WhatsApp Success',
      value: whatsapp.completedPercentage || 0,
      icon: <TrendingUp size={20} />,
      color: '#25D366',
      tooltip: 'Percentage of successful WhatsApp deliveries',
      isPercentage: true
    });

    // Always show failure rate
    metrics.push({
      title: 'WhatsApp Failure',
      value: whatsapp.failedPercentage || 0,
      icon: <TrendingDown size={20} />,
      color: '#ff4d4f',
      tooltip: 'Percentage of failed WhatsApp messages',
      isPercentage: true
    });

    return metrics;
  };

  const getEmailMetrics = () => {
    const emailServices = ['gmail', 'brevo', 'zeptomail'] as const;
    const emailStats = emailServices.map(service => currentStats[service]).filter(Boolean);

    // Always show basic email metrics even if no data
    const totalSent = emailStats.reduce((sum, stats) => sum + (stats!.sent || 0), 0);
    const totalDelivered = emailStats.reduce((sum, stats) => sum + (stats!.delivered || 0), 0);
    const totalFailed = emailStats.reduce((sum, stats) => sum + (stats!.failed || 0), 0);
    const avgFailureRate = emailStats.length > 0
      ? emailStats.reduce((sum, stats) => sum + stats!.failedPercentage, 0) / emailStats.length
      : 0;
    const avgSuccessRate = emailStats.length > 0
      ? emailStats.reduce((sum, stats) => sum + stats!.completedPercentage, 0) / emailStats.length
      : 0;

    const metrics = [];

    // Always show emails sent
    metrics.push({
      title: 'Emails Sent',
      value: totalSent,
      icon: <Mail size={20} />,
      color: '#1890ff',
      tooltip: 'Total email messages sent'
    });

    // Always show emails delivered
    metrics.push({
      title: 'Emails Delivered',
      value: totalDelivered,
      icon: <CheckCircle size={20} />,
      color: '#52c41a',
      tooltip: 'Total email messages delivered'
    });

    // Always show failed emails
    metrics.push({
      title: 'Failed Emails',
      value: totalFailed,
      icon: <XCircle size={20} />,
      color: '#ff4d4f',
      tooltip: 'Total email messages that failed to send'
    });

    return metrics;
  };

  const whatsappMetrics = getWhatsAppMetrics();
  const emailMetrics = getEmailMetrics();
  const allMetrics = [...whatsappMetrics, ...emailMetrics];

  if (allMetrics.length === 0) {
    return (
      <div className="metrics-display-container">
        <div className="metrics-header">
          <div className="metrics-title-section">
            <MessageCircle className="metrics-title-icon" />
            <Title level={4} className="metrics-title">Campaign Metrics</Title>
          </div>
          <Select
            value={selectedPeriod}
            onChange={setSelectedPeriod}
            className="period-selector"
            suffixIcon={<TrendingUp size={16} />}
          >
            <Option value="7">Last 7 Days</Option>
            <Option value="30">Last 30 Days</Option>
          </Select>
        </div>
        <div className="no-data-compact">
          <MessageCircle size={20} style={{ color: '#d9d9d9' }} />
          <Text style={{ color: '#8c8c8c', fontSize: '14px' }}>
            No metrics data available for the selected period
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className="metrics-display-container">
      <div className="metrics-header">
        <div className="metrics-title-section">
          <MessageCircle className="metrics-title-icon" />
          <Title level={4} className="metrics-title">Campaign Metrics</Title>
        </div>
        <Select
          value={selectedPeriod}
          onChange={setSelectedPeriod}
          className="period-selector"
          suffixIcon={<TrendingUp size={16} />}
        >
          <Option value="7">Last 7 Days</Option>
          <Option value="30">Last 30 Days</Option>
        </Select>
      </div>

      <div className="metrics-grid">
        {allMetrics.map((metric, index) => (
          <MetricCard key={index} {...metric} />
        ))}
      </div>
    </div>
  );
};

export default MetricsDisplay;